import { useState, type FormEvent } from 'react'
import type { TaskDTO } from '@wf/shared'
import { useAddTask, useDeleteTask, useToggleTask } from './useOrders'

/**
 * Aufgaben-Checkliste eines Auftrags. Abhaken togglet `done` (der Server setzt bei „alle erledigt"
 * den Auftragsstatus automatisch auf fertig). Hinzufügen/Entfernen nur mit Verwaltungsrecht; das Abhaken
 * darf auch der zugewiesene Mechaniker — der Server erzwingt beides ohnehin.
 */
export function OrderTasks({
  orderId,
  tasks,
  canToggle,
  canManage,
}: {
  orderId: number
  tasks: TaskDTO[]
  canToggle: boolean
  canManage: boolean
}) {
  const add = useAddTask(orderId)
  const toggle = useToggleTask(orderId)
  const remove = useDeleteTask(orderId)
  const [draft, setDraft] = useState('')
  const done = tasks.filter((task) => task.done).length

  function submitAdd(event: FormEvent) {
    event.preventDefault()
    const text = draft.trim()
    if (!text) return
    add.mutate(text, { onSuccess: () => setDraft('') })
  }

  return (
    <section className="wf-tasks">
      <span className="wf-label wf-sectiontitle">
        Aufgaben ({done}/{tasks.length})
      </span>
      <ul className="wf-tasklist">
        {tasks.map((task) => (
          <li key={task.id} className={task.done ? 'wf-task wf-task--done' : 'wf-task'}>
            <button
              type="button"
              className="wf-task__toggle"
              aria-pressed={Boolean(task.done)}
              disabled={!canToggle || toggle.isPending}
              onClick={() => toggle.mutate({ taskId: task.id, done: !task.done })}
            >
              <span className="wf-task__check" aria-hidden="true">
                {task.done ? '✓' : ''}
              </span>
              <span className="wf-task__text">{task.text}</span>
            </button>
            {canManage ? (
              <button
                type="button"
                className="wf-iconbtn"
                aria-label={`Aufgabe entfernen: ${task.text}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(task.id)}
              >
                ✕
              </button>
            ) : null}
          </li>
        ))}
        {tasks.length === 0 ? <li className="muted">Keine Aufgaben</li> : null}
      </ul>
      {canManage ? (
        <form className="wf-task-add" onSubmit={submitAdd}>
          <input
            className="wf-input"
            placeholder="Aufgabe hinzufügen …"
            aria-label="Neue Aufgabe"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <button type="submit" className="wf-btn wf-btn--ghost" disabled={add.isPending || !draft.trim()}>
            Hinzufügen
          </button>
        </form>
      ) : null}
    </section>
  )
}
